import React, { Fragment, useEffect, useState } from 'react';
import { useSpring, animated } from 'react-spring';
import { Formik, Form, Field } from 'formik';
import styled from '@emotion/styled';
import { GoogleLogin } from '@react-oauth/google';
import * as Yup from 'yup';
import { useTheme } from 'emotion-theming';
import { Global, css } from '@emotion/core';
import PropTypes from 'prop-types';

import Button from '../../../../shared/_TBComponents/Button';
import Checkmark from '../../../../shared/_TBComponents/Checkmark';
import Text from '../../../../shared/_TBComponents/Text';
import Link from '../../../../shared/_TBComponents/Link';
import encode from '../../../../shared/utils/encodeToUrl';
import useLogin from '../../../login/components/hooks/useLogin';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

const FormGroup = styled('div')`
  z-index: 1;
  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
`;

const FieldError = styled('div')`
  margin-top: 10px;
  font-size: 14px;
  color: #ff6600;
`;

export const SpinnerContainer = styled('div')`
  display: flex;
  height: 100%;
  flex: 1;
  position: absolute;
  top: 0;
  justify-content: center;
  width: 100%;
  z-index: 0;
`;

const LoginContainer = styled('div')`
  position: relative;
`;

const LoginSchema = Yup.object().shape({
  username: Yup.string()
    .email('Invalid Email Address')
    .required('Email Required'),
  password: Yup.string()
    .required('Password Required'),
});

const LoginModal = ({
  openModal,
  flags,
}) => {
  const {
    handleGoogleAuth,
    handleLogin,
    isLoginLoading,
    isAuthenticated,
    loginError,
    googleError,
  } = useLogin();

  const [googleLoginWidth, setGoogleLoginWidth] = useState('300');

  const formAnimation = useSpring({
    opacity: isLoginLoading || isAuthenticated ? 0 : 1,
    transform: isLoginLoading || isAuthenticated ? 'translateY(100%)' : 'translateY(0)',
    config: { duration: 500 },
    zIndex: 1,
    position: 'relative',
  });

  const spinnerAnimation = useSpring({ opacity: isLoginLoading ? 1 : 0, zIndex: 0 });
  const completeAnimation = useSpring({ opacity: isAuthenticated ? 1 : 0 });

  const theme = useTheme();

  const handleGoogleResponse = async (credentialResponse) => {
    const tokenId = credentialResponse.credential;

    if (!tokenId) {
      return;
    }

    const values = {
      grant_type: 'google',
      client_id: process.env.CLIENT_ID,
      client_secret: process.env.CLIENT_SECRET,
      tokenid: tokenId,
      scope: 'offline_access',
    };

    handleGoogleAuth(encode(values));
  };

  useEffect(() => {
    const handleResize = () => {
      setGoogleLoginWidth(window.innerWidth < 400 ? '270' : '300');
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Formik
      initialValues={{
        grant_type: 'password',
        username: '',
        password: '',
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        scope: 'offline_access',
      }}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(() => {
          handleLogin(encode(values));
          // alert(JSON.stringify(encode(values), null, 2));
          setSubmitting(false);
        });
      }}
      validationSchema={LoginSchema}
      render={({
        errors,
        touched,
        handleSubmit,
      }) => (
        <LoginContainer>
          <Global
            styles={css`
              .ReactModal__Content {
                width: 420px!important;
              }
            `}
          />
          <>
            <animated.div style={formAnimation}>
              <Form
                name='login'
                id='login'
              >
                <FormGroup className='form-group'>
                  <Text
                    styledTextProps={{ fontSize: theme.textFontSizes[0], fontWeight: '700', marginBottom: '8px !important' }}
                  >
                    Email Address
                  </Text>
                  <Field
                    className='form-control'
                    id='email-field'
                    type='email'
                    name='username'
                    placeholder='Email'
                  />
                  {errors.username && touched.username && <FieldError className='text-center'>{errors.username}</FieldError>}
                </FormGroup>
                <FormGroup className='form-group'>
                  <Text
                    styledTextProps={{ fontSize: theme.textFontSizes[0], fontWeight: '700', marginBottom: '8px !important' }}
                  >
                    Password
                  </Text>
                  <Field
                    className='form-control'
                    type='password'
                    name='password'
                    placeholder='Password'
                  />
                  {errors.password && touched.password && <FieldError className='text-center'>{errors.password}</FieldError>}
                  {loginError && <FieldError className='text-center'>Your email address or password is incorrect! Please try again.</FieldError>}
                  {googleError && <FieldError className='text-center'>This google account does not seem to be associated with a TeamBuildr account.</FieldError>}
                </FormGroup>
                <FormGroup className='text-center'>
                  <Button
                    bgColor={theme.colors.orange}
                    disabled={errors.password || errors.username}
                    fontSize='14px'
                    fullWidth
                    large
                    name='Sign In'
                    noBorder
                    onClick={handleSubmit}
                    rounded
                    styledWrapperProps={{
                      marginBottom: '20px',
                    }}
                    styledTextProps={{
                      marginBottom: '0px !important',
                    }}
                    txtColor={theme.colors.white}
                    upperCase
                  />
                  <GoogleLogin
                    locale='en'
                    onSuccess={handleGoogleResponse}
                    text='signin_with'
                    width={googleLoginWidth}
                  />
                  <Button
                    fontSize='14px'
                    fullWidth
                    large
                    name='Don&apos;t have an account? Click here to create one'
                    noBorder
                    onClick={() => openModal('createAccount')}
                    styledWrapperProps={{ margin: '30px 0px 15px' }}
                    txtColor={theme.colors.lightText[1]}
                    type='button'
                    upperCase
                  />
                  {flags.registration ? (
                    <Link allCaps className='easy-join' color='lightText.1' href='/signup/easy-join'>
                      Have an Easy Join Code? Click Here
                    </Link>
                  ) : (
                    <Link allCaps className='easy-join' color='lightText.1' href='https://app.teambuildr.com/signup?ej=true'>
                      Have an Easy Join Code? Click Here
                    </Link>
                  )}
                  <Link allCaps color='lightText.2' href='/forgot'>
                    Forgot Your Password?
                  </Link>

                </FormGroup>
              </Form>
            </animated.div>
            <animated.div style={spinnerAnimation}>
              <SpinnerContainer>
                <Spinner />
              </SpinnerContainer>
            </animated.div>
            <animated.div style={completeAnimation}>
              <SpinnerContainer>
                <Checkmark />
              </SpinnerContainer>
            </animated.div>
          </>
        </LoginContainer>
      )}
    />
  );
};

LoginModal.propTypes = {
  openModal: PropTypes.func.isRequired,
};

export default withLDConsumer()(LoginModal);
